import styles from "./SourcingIntelligence.module.css";
import { parseINR } from "./SourcingIntelligence";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const VENDOR_COLORS = [
  "#1565C0",
  "#0288D1",
  "#7B1FA2",
  "#00838F",
  "#2E7D32",
  "#E65100",
  "#C62828",
  "#558B2F",
];

function formatCompactINR(v) {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
  return `₹${Number(v).toLocaleString("en-IN")}`;
}

export function AnnualSpendTrend({ yearData }) {
  const points = [...(yearData || [])]
    .sort((a, b) => Number(a.year) - Number(b.year))
    .map((y) => ({ year: y.year, spend: parseINR(y?.annual_spend?.total_spend) }));

  if (points.length < 2) return null;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className={styles.chartTooltip}>
        <div className={styles.chartTooltipTitle}>FY {label}</div>
        <div className={styles.chartTooltipVal}>{formatCompactINR(payload[0].value)}</div>
      </div>
    );
  };

  return (
    <div className={styles.trendCard}>
      <div className={styles.chartCardTitle}>Annual Spend Trend</div>
      <div className={styles.chartCardSub}>Year-over-year comparison across all fetched years</div>
      <div className={styles.trendChartWrap}>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={points} margin={{ top: 10, right: 18, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="annualSpendArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#42A5F5" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#42A5F5" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#E6EEF6" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fill: "#546E7A", fontWeight: 700, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#90A4AE", fontWeight: 700, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={76}
              tickFormatter={(v) =>
                v >= 10000000
                  ? `${(v / 10000000).toFixed(1)}Cr`
                  : v >= 100000
                  ? `${(v / 100000).toFixed(1)}L`
                  : `${(v / 1000).toFixed(0)}K`
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="spend"
              stroke="#42A5F5"
              strokeWidth={2.2}
              fill="url(#annualSpendArea)"
              dot={{ r: 3.5, strokeWidth: 2, fill: "#fff" }}
              activeDot={{ r: 5, strokeWidth: 2, fill: "#fff" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MonthlySpendChart({ monthlySpend }) {
  if (!monthlySpend || monthlySpend.length === 0) return null;
  const max = Math.max(...monthlySpend.map((m) => parseINR(m.total_spend)));

  const fmtShort = (v) => {
    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
    return `₹${(v / 1000).toFixed(0)}K`;
  };

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartCardTitle}>Monthly Spend Trend</div>
      <div className={styles.chartCardSub}>Total procurement spend per month (active year)</div>
      {monthlySpend.map((m, i) => {
        const val = parseINR(m.total_spend);
        const pct = max > 0 ? (val / max) * 100 : 0;
        return (
          <div key={m.month} className={styles.barRow}>
            <div className={styles.barRowTop}>
              <span className={styles.barLabel}>{m.month}</span>
              <span className={styles.barValue}>{fmtShort(val)}</span>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{
                  width: `${pct}%`,
                  background: VENDOR_COLORS[i % VENDOR_COLORS.length],
                }}
              />
            </div>
            <div className={styles.barSub}>
              {m.purchase_count} purchase{m.purchase_count !== 1 ? "s" : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VendorDistributionChart({ itemwise }) {
  if (!itemwise || itemwise.length === 0) return null;

  const vendorMap = {};
  itemwise.forEach((item) => {
    vendorMap[item.vendor_name] = (vendorMap[item.vendor_name] || 0) + parseINR(item.total_price);
  });

  const sorted = Object.entries(vendorMap).sort(([, a], [, b]) => b - a);
  const total = sorted.reduce((s, [, v]) => s + v, 0);
  const max = sorted[0]?.[1] || 1;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartCardTitle}>Supplier-wise Spend</div>
      <div className={styles.chartCardSub}>Total spend distribution across vendors (active year)</div>
      {sorted.map(([vendor, val], i) => {
        const pct = max > 0 ? (val / max) * 100 : 0;
        const share = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
        return (
          <div key={vendor} className={styles.barRow}>
            <div className={styles.barRowTop}>
              <span className={styles.barLabel} title={vendor}>
                {vendor}
              </span>
              <span className={styles.barValue}>{formatCompactINR(val)}</span>
            </div>
            <div className={styles.barTrack}>
              <div
                className={styles.barFill}
                style={{
                  width: `${pct}%`,
                  background: VENDOR_COLORS[i % VENDOR_COLORS.length],
                }}
              />
            </div>
            <div className={styles.barSub}>{share}% of total spend</div>
          </div>
        );
      })}
    </div>
  );
}

export default function SpendCharts({ activeYearData }) {
  return (
    <div className={styles.chartsGrid}>
      <MonthlySpendChart monthlySpend={activeYearData?.monthly_spend} />
      <VendorDistributionChart itemwise={activeYearData?.itemwise} />
    </div>
  );
}
